import React from 'react';


/**
 * Assistant guidé de complétion des pièces d'un dossier AO.
 *
 * Composant purement présentationnel : le curseur (file d'attente, index borné,
 * pièce courante) est calculé par un helper unique côté DossiersAO et passé ici
 * via `cursor` — l'affichage et les actions lisent donc toujours la même pièce.
 */

const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

// Aide contextuelle, une phrase par type de pièce.
export const PIECE_HELP = {
  cni: 'Pièce d\'identité — recto/verso lisible, en cours de validité.',
  permis: 'Permis de travail suisse (B, C, G…) ou attestation frontalier.',
  cv: 'CV au format imposé par le client — l\'IA le génère depuis le CV source.',
  matrice: 'Matrice de compétences exigée par le cahier des charges du lot.',
};

/** Famille d'une pièce à partir de son libellé (sert au tri, à l'aide et aux phases). */
export const pieceKind = (label) => {
  const l = (label || '').toLowerCase();
  if (l.includes('cni') || l.includes('identit')) return 'cni';
  if (l.includes('permis')) return 'permis';
  if (l.includes('cv')) return 'cv';
  if (l.includes('matrice')) return 'matrice';
  return 'autre';
};

/** Administratif d'abord, puis CV TAT, puis matrice, puis le reste. */
export const piecePriority = (label) => ({ cni: 1, permis: 1, cv: 2, matrice: 3 }[pieceKind(label)] || 4);

const PHASE = {
  1: { label: 'Administratif', admin: true },
  2: { label: 'Dossier candidat', admin: false },
  3: { label: 'Compétences', admin: false },
  4: { label: 'Complément', admin: false },
};

const STATUS_CHIP = {
  a_generer: { label: 'À générer', bg: 'rgba(217,119,6,0.14)', fg: '#d97706' },
  a_uploader: { label: 'À copier sur kDrive', bg: 'rgba(37,99,235,0.14)', fg: '#2563eb' },
  a_fournir: { label: 'Manquant', bg: 'var(--danger-bg)', fg: 'var(--danger-fg)' },
};

const DOC_STATE = {
  present: { dot: 'rgba(45,200,110,.18)', ink: '#16a34a', tag: 'au dossier', icon: 'check-lg' },
  done: { dot: 'rgba(45,200,110,.18)', ink: '#16a34a', tag: 'ajouté à l\'instant', icon: 'check-lg' },
  current: { dot: 'var(--accent)', ink: 'var(--text-strong)', tag: 'en cours', icon: 'arrow-down' },
  pending: { dot: 'var(--code-bg)', ink: 'var(--text-faint)', tag: 'en attente', icon: null },
};

const initials = (name) => (name || '?').split(/[\s_-]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 15px', borderRadius: '9px',
  fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: '1px solid var(--input-border)',
  background: 'var(--ghost-bg)', color: 'var(--text-2)',
};
const btnMain = { ...btn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontWeight: 700 };
const btnPlain = { ...btn, border: 'none', background: 'transparent', color: 'var(--text-3)' };

export default function AoAssistant({ cursor, busy, aoId, onToast, onIdx, onUpload, onGenerate, onAsk, onClose }) {
  const { pend = [], idx, cur, docs, total, doneCount } = cursor;
  const n = pend.length;

  // Les animations doivent rejouer à chaque pièce : React réutilise le nœud, on
  // alterne donc le nom de l'animation entre deux keyframes identiques.
  const tickRef = React.useRef(0);
  const lastKey = React.useRef(null);
  if (cur && lastKey.current !== cur.key) { lastKey.current = cur.key; tickRef.current += 1; }
  const alt = tickRef.current % 2 ? 'Alt' : '';

  const shell = (children) => (
    <div style={{ marginBottom: '18px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
      {children}
    </div>
  );

  // ── Dossier complet ──
  if (!cur) return shell(
    <div style={{ padding: '26px 22px', textAlign: 'center' }}>
      <div style={{ width: '42px', height: '42px', margin: '0 auto', borderRadius: '50%', background: 'var(--accent)', color: 'var(--accent-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '19px' }}><BI icon="check-lg" /></div>
      <div style={{ marginTop: '12px', fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>Dossier complet</div>
      <div style={{ marginTop: '4px', fontSize: '13px', color: 'var(--text-2)' }}>{doneCount} pièce{doneCount > 1 ? 's' : ''} traitée{doneCount > 1 ? 's' : ''} — le dossier est prêt à être déposé.</div>
      <button onClick={onClose} style={{ ...btn, marginTop: '16px' }}>Fermer l'assistant</button>
    </div>
  );

  const kind = pieceKind(cur.label);
  // Chaque mode construit ses propres styles : le rouge d'alerte de la file
  // documents n'a rien à faire sur le chip du mode CV.
  const phase = PHASE[cur.prio] || PHASE[4];
  const chip = STATUS_CHIP[cur.status] || STATUS_CHIP.a_fournir;
  const adminLeft = pend.filter(p => p.prio === 1).length;
  // Le total surprend vite : c'est l'ensemble des pièces manquantes du dossier,
  // tous lots confondus. On rappelle donc combien de candidats sont concernés.
  const candCount = new Set(pend.map(p => p.candidat)).size;
  const nexts = Array.from({ length: Math.min(3, n - 1) }, (_, i) => pend[(idx + 1 + i) % n]);
  const generable = kind !== 'cni' && kind !== 'permis';

  return shell(<>
    {/* Barre de titre */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '14px 18px 12px', flexWrap: 'wrap' }}>
      <span style={{ flex: 'none', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: 'var(--code-bg)', color: 'var(--text-2)' }}>✦ Assistant dossier</span>
      <div style={{ flex: 1, minWidth: '160px' }}>
        <div title={`Toutes les pièces manquantes du dossier AO, tous lots et tous candidats confondus (${candCount} candidat${candCount > 1 ? 's' : ''} concerné${candCount > 1 ? 's' : ''}).`}
          style={{ fontFamily: "'Archivo',sans-serif", fontSize: '15px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>
          Pièce {doneCount + idx + 1} sur {total} à compléter
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '1px' }}>
          {candCount} candidat{candCount > 1 ? 's' : ''} du dossier · dont {adminLeft} pièce{adminLeft > 1 ? 's' : ''} administrative{adminLeft > 1 ? 's' : ''} (CNI, permis)
        </div>
      </div>
      <button onClick={() => onClose()} title="Fermer (l'avancement est conservé)" style={{ ...btnPlain, width: '32px', height: '32px', padding: 0, justifyContent: 'center' }}><BI icon="x-lg" /></button>
    </div>

    {/* Progression : pièces traitées, ou améliorations appliquées sur le lot */}
    <div style={{ height: '4px', background: 'var(--code-bg)' }}>
      <div style={{ height: '100%', width: `${total ? Math.min(100, (doneCount / total) * 100) : 0}%`, background: 'var(--accent)', transition: 'width .3s ease' }} />
    </div>

    {/* Animations : deux variantes identiques par effet, pour pouvoir rejouer. */}
    <style>{`
      @keyframes folderOpen{from{opacity:0;transform:translateY(12px) scale(.96)}to{opacity:1;transform:none}}
      @keyframes folderOpenAlt{from{opacity:0;transform:translateY(12px) scale(.96)}to{opacity:1;transform:none}}
      @keyframes folderTabFlip{0%{transform:rotate(-6deg) scale(.85)}70%{transform:rotate(2deg) scale(1.05)}100%{transform:none}}
      @keyframes folderTabFlipAlt{0%{transform:rotate(-6deg) scale(.85)}70%{transform:rotate(2deg) scale(1.05)}100%{transform:none}}
      @keyframes docRowIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
      @keyframes docRowInAlt{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
      @keyframes sugIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:none}}
    `}</style>

    {/* Pièce courante */}
    <div style={{ padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', flexWrap: 'wrap' }}>
        <span style={{ width: '38px', height: '38px', flex: 'none', borderRadius: '10px', background: 'var(--accent-soft-bg)', color: 'var(--text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700 }}>{initials(cur.candidat)}</span>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{cur.candidat}</span>
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: phase.admin ? 'var(--danger-bg)' : 'var(--chip-bg)', color: phase.admin ? 'var(--danger-fg)' : 'var(--text-2)' }}>{phase.label}</span>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '2px', wordBreak: 'break-word' }}>{cur.lotRef}</div>
        </div>
      </div>

      {/* Le dossier kDrive du candidat, qui s'ouvre à chaque pièce */}
      {(docs || []).length > 0 && (
        <div style={{ position: 'relative', marginTop: '20px', padding: '15px 14px 12px', background: 'var(--panel, var(--surface))', border: '1px solid var(--input-border)', borderRadius: '12px', animation: `folderOpen${alt} .36s cubic-bezier(.22,.61,.36,1) both` }}>
          <span style={{ position: 'absolute', left: '14px', top: '-10px', height: '20px', display: 'inline-flex', alignItems: 'center', padding: '0 9px', borderRadius: '7px 7px 0 0', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: "'IBM Plex Mono',monospace", fontSize: '9.5px', letterSpacing: '.06em', transformOrigin: 'bottom left', animation: `folderTabFlip${alt} .42s cubic-bezier(.22,.61,.36,1) both` }}>
            {(cur.candidat || '').replace(/\s+/g, '_')}/
          </span>
          <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10px', color: 'var(--text-faint)', letterSpacing: '.05em', marginBottom: '9px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Dossier du candidat · {cur.lotRef}
          </div>
          {docs.map((d, i) => {
            const st = DOC_STATE[d.state] || DOC_STATE.pending;
            // En mode CV, c'est la ligne du CV qui est mise en avant.
            const active = d.state === 'current';
            return (
              <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0, padding: active ? '6px 8px' : '6px 0', marginBottom: '2px', borderRadius: active ? '8px' : 0, background: active ? 'var(--code-bg)' : 'transparent', border: active ? '1px solid var(--input-border)' : '1px solid transparent', animation: `docRowIn${alt} .34s cubic-bezier(.22,.61,.36,1) both`, animationDelay: `${90 + i * 55}ms` }}>
                <span style={{ width: '16px', height: '16px', flex: 'none', borderRadius: '50%', background: st.dot, color: d.state === 'current' ? 'var(--accent-fg)' : st.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px' }}>
                  {st.icon && <BI icon={st.icon} />}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', fontWeight: active ? 600 : 400, color: st.ink }}>{d.label}</span>
                <span style={{ flex: 'none', fontFamily: "'IBM Plex Mono',monospace", fontSize: '9.5px', letterSpacing: '.05em', textTransform: 'uppercase', color: d.state === 'pending' ? 'var(--text-faint)' : st.ink }}>{d.cvTag || st.tag}</span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: '13px', padding: '13px 14px', borderRadius: '11px', background: 'var(--code-bg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Il manque : {cur.label}</span>
          <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: chip.bg, color: chip.fg }}>{chip.label}</span>
        </div>
        {PIECE_HELP[kind] && <div style={{ marginTop: '6px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>{PIECE_HELP[kind]}</div>}
        {/* Relance déjà partie : on évite de redemander sans le savoir. */}
        {cur.requestedAt && !Number.isNaN(new Date(cur.requestedAt).getTime()) && (
          <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-3)' }}>
            <BI icon="send" style={{ marginRight: '5px' }} />Demandé le {new Date(cur.requestedAt).toLocaleDateString('fr-FR')}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '13px', flexWrap: 'wrap' }}>
        <button onClick={() => onUpload(cur)} disabled={busy}
          title={cur.status === 'a_uploader' ? 'Le document est dans l\'ERP mais pas dans le dossier kDrive — le copier maintenant' : 'Choisir un fichier à déposer dans le dossier kDrive'}
          style={{ ...btnMain, opacity: busy ? 0.6 : 1 }}>
          <BI icon="cloud-arrow-up" /> {cur.status === 'a_uploader' ? 'Copier sur kDrive' : 'Uploader'}
        </button>
        {generable && <button onClick={() => onGenerate(cur)} disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>✦ Générer avec l'IA</button>}
        <button onClick={() => onAsk(cur)} disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}><BI icon="send" /> Demander</button>
        <button onClick={() => onIdx(1)} style={btnPlain}>Plus tard</button>
      </div>
    </div>

    {/* Pied : navigation + aperçu */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 18px', borderTop: '1px solid var(--hair)', flexWrap: 'wrap' }}>
      <button onClick={() => onIdx(-1)} style={btnPlain}><BI icon="arrow-left" /> Précédente</button>
      {nexts.length > 0 && <>
        <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-faint)' }}>Ensuite</span>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          {nexts.map((it, i) => (
            <span key={i} title={`${it.candidat} · ${it.label}`}
              style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', maxWidth: '230px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: it.prio === 1 ? 'var(--danger-bg)' : 'var(--chip-bg)', color: it.prio === 1 ? 'var(--danger-fg)' : 'var(--text-2)' }}>
              {it.candidat} · {it.label}
            </span>
          ))}
        </div>
      </>}
      <div style={{ flex: nexts.length ? 'none' : 1 }} />
      <span style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>
        {`${doneCount} traitée${doneCount > 1 ? 's' : ''}`}
      </span>
    </div>
  </>);
}
